import { useEffect, useState } from 'react';
import styled from 'styled-components';
import FormModal from '@/shared/components/form-modal';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { MEDIA_POLICY_LABELS, MEDITATION_TYPE_LABELS, deriveLiveSessionStatus } from '@/models/live-meditation-session';
import type { LiveSessionMeditationType, MediaPolicy, RoomPrivacy } from '@/models/live-meditation-session';
import { useCoaches, useCreateLiveSession, useUpdateLiveSession, type CreateLiveSessionPayload } from '@/services/live-sessions';
import type LiveMeditationSession from '@/models/live-meditation-session';
import GuestPicker from './guest-picker';

interface Props {
    isOpen: boolean;
    toggle: () => void;
    /** Present = EDIT an existing session. Absent = create a new one. */
    session?: LiveMeditationSession | null;
}

// `guided` is deliberately absent: a guided session needs a `contents` row and the
// API rejects one whose `contentId` does not resolve, so offering it without a
// track picker would be a button that always 400s.
const MEDITATION_TYPES: LiveSessionMeditationType[] = ['breathing', 'body-scan', 'silent'];
const POLICIES: MediaPolicy[] = ['free', 'on-request', 'disabled'];

/** Sentinel for "the admin creating it hosts" — see the picker below. */
const SELF_HOST = 'self';

const emptyForm = {
    title: '',
    description: '',
    meditationType: 'breathing' as LiveSessionMeditationType,
    durationMinutes: '45',
    privacy: 'public' as RoomPrivacy,
    // The guest list. For a PRIVATE room this IS the door — the API's
    // `canJoinLiveSession` reads nothing else and fails closed — so a private
    // session saved with an empty list is a room only its host can enter.
    accessUserIds: [] as string[],
    scheduledAtLocal: '',
    coachUserId: SELF_HOST,
    maxParticipants: '50',
    audioPolicy: 'on-request' as MediaPolicy,
    videoPolicy: 'on-request' as MediaPolicy,
    waitingRoom: false,
    // These mirror `defaultLiveRoomSettings` on the server. Muted-on-arrival is
    // deliberately the default: a room that opened every microphone the moment
    // somebody shared the link is not a default anyone would choose on purpose.
    participantJoinMuted: true,
    participantJoinCameraOff: true,
    raiseHandEnabled: true,
    reactionsEnabled: true,
    chatEnabled: true,
    // Recurrence turns a one-off into a standing anchor session — the answer to
    // "nothing is ever running". A recurring TEMPLATE is never joined itself; it
    // opens a dated occurrence before each start time.
    repeats: 'none' as 'none' | 'daily' | 'weekly',
    repeatTime: '08:00',
    repeatDays: [] as number[],
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** `datetime-local` wants `YYYY-MM-DDTHH:mm` in LOCAL time, not an ISO instant. */
const isoToLocalInput = (iso?: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Create a coach-led live session.
 *
 * The A/V policies are the most consequential fields here, so they are labelled
 * with their consequence rather than the bare enum value: the host is deciding
 * what everybody who joins will and will not be able to do, and deciding it
 * BEFORE anybody has the link. "Participants cannot speak" is
 * `audioPolicy: disabled` — a setting on this form, not a property of the room.
 */
const CreateSessionModal = ({ isOpen, toggle, session }: Props) => {
    const [form, setForm] = useState(emptyForm);
    const [error, setError] = useState<string | null>(null);
    const { data: coachesResponse } = useCoaches();
    const { mutateAsync: create, isPending: creating } = useCreateLiveSession();
    const { mutateAsync: update, isPending: updating } = useUpdateLiveSession();
    const isEditing = !!session;
    // DERIVED, not `session.status` — the stored value lags until the sweep tidies
    // it, so a session whose time has passed still reads `live` and would not
    // offer the renew flow.
    const derived = session ? deriveLiveSessionStatus(session) : null;
    const isFinished = derived === 'ended' || derived === 'cancelled';
    const isPending = creating || updating;

    // Reset when the modal OPENS. `isOpen` in the deps is the admin FormModal
    // convention — without it a second create silently reuses the first one's
    // values.
    useEffect(() => {
        if (!isOpen) return;
        setError(null);
        if (!session) {
            setForm(emptyForm);
            return;
        }
        // RENEWING a finished session needs a NEW time — and a session that was
        // started by hand has no `scheduledAt` at all, so the field would open
        // blank, save as `null`, and the server's reset would never fire. That is
        // exactly why renew appeared to do nothing. Prefill tomorrow at the same
        // hour so the common case is one click.
        const finished = deriveLiveSessionStatus(session) === 'ended' || deriveLiveSessionStatus(session) === 'cancelled';
        const existingWhen = isoToLocalInput(session.scheduledAt);
        const suggested = (() => {
            const d = new Date();
            d.setDate(d.getDate() + 1);
            d.setMinutes(0, 0, 0);
            return isoToLocalInput(d.toISOString());
        })();

        setForm({
            ...emptyForm,
            title: session.title,
            description: session.description ?? '',
            meditationType: session.meditationType,
            durationMinutes: String(session.durationMinutes),
            privacy: session.privacy,
            // The API returns this to the HOST only; for anybody else it comes
            // back empty, which is why it must never be treated as authoritative
            // on a session somebody else created.
            accessUserIds: session.accessUserIds ?? [],
            scheduledAtLocal: finished
                ? existingWhen && new Date(session.scheduledAt as string) > new Date()
                    ? existingWhen
                    : suggested
                : existingWhen,
            coachUserId: session.hostUserId || SELF_HOST,
            maxParticipants: String(session.settings?.maxParticipants ?? 50),
            audioPolicy: session.settings?.audioPolicy ?? 'on-request',
            videoPolicy: session.settings?.videoPolicy ?? 'on-request',
            waitingRoom: !!session.settings?.waitingRoom,
            // `?? true` rather than `!!` — these default to ON, so a session saved
            // before they were editable must not read as though staff turned them off.
            participantJoinMuted: session.settings?.participantJoinMuted ?? true,
            participantJoinCameraOff: session.settings?.participantJoinCameraOff ?? true,
            raiseHandEnabled: session.settings?.raiseHandEnabled ?? true,
            reactionsEnabled: session.settings?.reactionsEnabled ?? true,
            chatEnabled: session.settings?.chatEnabled ?? true,
            repeats: session.recurrence?.active ? session.recurrence.frequency : 'none',
            repeatTime: session.recurrence?.timeOfDay ?? '08:00',
            repeatDays: session.recurrence?.daysOfWeek ?? [],
        });
    }, [isOpen, session]);

    const coaches = coachesResponse?.data?.coaches ?? [];
    const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((prev) => ({ ...prev, [key]: value }));

    const handleSave = async () => {
        setError(null);
        if (!form.title.trim()) {
            setError('Give the session a name.');
            return;
        }
        // A finished session only comes back if it is given a time in the FUTURE
        // — the server resets the lifecycle off exactly that. Saying so here beats
        // a silent no-op that looks like a broken button.
        if (isFinished) {
            const when = form.scheduledAtLocal ? new Date(form.scheduledAtLocal) : null;
            if (!when || when.getTime() <= Date.now()) {
                setError('Pick a future start time to bring this session back.');
                return;
            }
        }

        const payload: CreateLiveSessionPayload = {
            title: form.title.trim(),
            meditationType: form.meditationType,
            durationMinutes: Number(form.durationMinutes),
            privacy: form.privacy,
            // The datetime-local input yields wall-clock with no zone. `new Date`
            // reads it in the BROWSER's zone, which is what the admin meant, and we
            // send the absolute instant — so attendees see it in their own local
            // time rather than the admin's.
            scheduledAt: form.scheduledAtLocal ? new Date(form.scheduledAtLocal).toISOString() : null,
            settings: {
                maxParticipants: Number(form.maxParticipants),
                audioPolicy: form.audioPolicy,
                videoPolicy: form.videoPolicy,
                waitingRoom: form.waitingRoom,
                participantJoinMuted: form.participantJoinMuted,
                participantJoinCameraOff: form.participantJoinCameraOff,
                raiseHandEnabled: form.raiseHandEnabled,
                reactionsEnabled: form.reactionsEnabled,
                chatEnabled: form.chatEnabled,
            },
        };
        // Only send the optional keys when they hold a value: the API's validate()
        // rejects unmatched body keys, and an empty string is a real value that
        // fails the notEmpty check rather than being ignored.
        if (form.description.trim()) payload.description = form.description.trim();
        // Sent only for a PRIVATE room: the server clears the list on a public one,
        // so sending it there would imply a guest list that means nothing.
        if (form.privacy === 'private') payload.accessUserIds = form.accessUserIds;
        if (form.coachUserId && form.coachUserId !== SELF_HOST) payload.coachUserId = form.coachUserId;

        // `null` CLEARS a recurrence, which an omitted key would not — turning a
        // standing session back into a one-off has to be expressible.
        payload.recurrence =
            form.repeats === 'none'
                ? null
                : {
                      frequency: form.repeats,
                      timeOfDay: form.repeatTime,
                      // The admin's own zone: they enter a wall clock, and it must
                      // stay that wall clock through daylight saving.
                      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                      daysOfWeek: form.repeats === 'weekly' ? form.repeatDays : undefined,
                      active: true,
                  };

        const response = isEditing ? await update({ sessionId: session.id, ...payload }) : await create(payload);
        if (response.status === 'error') {
            setError(response.error?.message || 'Could not create the session.');
            return;
        }
        toggle();
    };

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={false}
            loading={false}
            title={isFinished ? 'Renew live session' : isEditing ? 'Edit live session' : 'New live session'}
            toggle={toggle}
            handleDelete={() => undefined}
            handleSave={handleSave}
            saveDisabled={isPending}
            saveDisabledHint={isPending ? (isEditing ? 'Saving…' : 'Creating…') : undefined}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && <ErrorBox>{error}</ErrorBox>}

                    <MaterialTextField label="Title" value={form.title} onChange={(e) => set('title', e.target.value)} />
                    <MaterialTextField
                        label="Description (optional)"
                        multiline
                        value={form.description}
                        onChange={(e) => set('description', e.target.value)}
                    />

                    <MaterialTextField
                        name="coach"
                        select
                        label="Who leads it?"
                        value={form.coachUserId}
                        onChange={(e) => set('coachUserId', e.target.value)}
                    >
                        <MaterialFormOption value="">Me (the admin creating it)</MaterialFormOption>
                        {coaches.map((coach) => (
                            <MaterialFormOption key={coach.id} value={coach.id}>
                                {coach.name} — {coach.email}
                            </MaterialFormOption>
                        ))}
                    </MaterialTextField>
                    {coaches.length === 0 && <Hint>No users have the “coach” role yet — set it on a user in Users to list them here.</Hint>}

                    <MaterialTextField
                        name="meditationType"
                        select
                        label="Type"
                        value={form.meditationType}
                        onChange={(e) => set('meditationType', e.target.value as LiveSessionMeditationType)}
                    >
                        {MEDITATION_TYPES.map((type) => (
                            <MaterialFormOption key={type} value={type}>
                                {MEDITATION_TYPE_LABELS[type]}
                            </MaterialFormOption>
                        ))}
                    </MaterialTextField>

                    <MaterialTextField
                        label="Length (minutes)"
                        type="number"
                        value={form.durationMinutes}
                        onChange={(e) => set('durationMinutes', e.target.value)}
                    />
                    <Hint>
                        Up to 2 hours. That ceiling is a cost guardrail — a live room&apos;s audio and video cross Cloudflare and are billed on
                        bandwidth.
                    </Hint>

                    <MaterialTextField
                        label={isFinished ? 'New start time (required to bring it back)' : 'Starts at (optional)'}
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        value={form.scheduledAtLocal}
                        onChange={(e) => set('scheduledAtLocal', e.target.value)}
                    />
                    <Hint>
                        {isFinished
                            ? 'This session has finished. Giving it a future time brings it back as a fresh run — the old roster and timings are cleared.'
                            : 'Leave blank to start it by hand. You enter YOUR local time; attendees always see it in their own.'}
                    </Hint>

                    <MaterialTextField
                        name="privacy"
                        select
                        label="Who can join?"
                        value={form.privacy}
                        onChange={(e) => set('privacy', e.target.value as RoomPrivacy)}
                    >
                        <MaterialFormOption value="public">Public — any member can join</MaterialFormOption>
                        <MaterialFormOption value="private">Private — invited members only</MaterialFormOption>
                    </MaterialTextField>

                    {form.privacy === 'private' && (
                        <GuestPicker value={form.accessUserIds} onChange={(accessUserIds) => set('accessUserIds', accessUserIds)} />
                    )}

                    <SectionTitle>Repeat</SectionTitle>
                    <p className="form__form-group-description" style={{ marginBottom: 12 }}>
                        A repeating session runs on its own, for ever, until you turn it off. That is what stops the app being empty — one reliable
                        slot people can plan around beats several one-offs nobody catches. The repeating entry is a SCHEDULE, not a room: it opens a
                        fresh room shortly before each start time, and anybody following it is reminded every time.
                    </p>
                    <MaterialTextField
                        name="repeats"
                        select
                        label="Repeats"
                        value={form.repeats}
                        onChange={(e) => set('repeats', e.target.value as 'none' | 'daily' | 'weekly')}
                    >
                        <MaterialFormOption value="none">Doesn&apos;t repeat — a single session</MaterialFormOption>
                        <MaterialFormOption value="daily">Every day</MaterialFormOption>
                        <MaterialFormOption value="weekly">Every week, on chosen days</MaterialFormOption>
                    </MaterialTextField>

                    {form.repeats !== 'none' && (
                        <>
                            <MaterialTextField
                                label="Starts at (your local time)"
                                type="time"
                                InputLabelProps={{ shrink: true }}
                                value={form.repeatTime}
                                onChange={(e) => set('repeatTime', e.target.value)}
                            />
                            <Hint>Stays this wall-clock time through daylight saving — 8am does not drift to 7am in winter.</Hint>
                        </>
                    )}

                    {form.repeats === 'weekly' && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                            {DAY_LABELS.map((label, index) => {
                                const on = form.repeatDays.includes(index);
                                return (
                                    <button
                                        key={label}
                                        type="button"
                                        onClick={() =>
                                            set('repeatDays', on ? form.repeatDays.filter((d) => d !== index) : [...form.repeatDays, index].sort())
                                        }
                                        style={{
                                            border: `1px solid ${on ? '#4ce1b6' : '#555555'}`,
                                            background: on ? 'rgba(76,225,182,0.15)' : 'transparent',
                                            color: on ? '#4ce1b6' : '#999999',
                                            borderRadius: 999,
                                            padding: '4px 12px',
                                            fontSize: 12,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <SectionTitle>What participants may do</SectionTitle>
                    <Hint>
                        These apply to everyone except the host, and they are set before anybody has the link — so choose them as if the room were
                        already full.
                    </Hint>

                    <MaterialTextField
                        name="audioPolicy"
                        select
                        label="Microphones"
                        value={form.audioPolicy}
                        onChange={(e) => set('audioPolicy', e.target.value as MediaPolicy)}
                    >
                        {POLICIES.map((policy) => (
                            <MaterialFormOption key={policy} value={policy}>
                                {MEDIA_POLICY_LABELS[policy]}
                            </MaterialFormOption>
                        ))}
                    </MaterialTextField>

                    <MaterialTextField
                        name="videoPolicy"
                        select
                        label="Cameras"
                        value={form.videoPolicy}
                        onChange={(e) => set('videoPolicy', e.target.value as MediaPolicy)}
                    >
                        {POLICIES.map((policy) => (
                            <MaterialFormOption key={policy} value={policy}>
                                {MEDIA_POLICY_LABELS[policy]}
                            </MaterialFormOption>
                        ))}
                    </MaterialTextField>

                    <MaterialTextField
                        label="Capacity"
                        type="number"
                        value={form.maxParticipants}
                        onChange={(e) => set('maxParticipants', e.target.value)}
                    />
                    <Hint>A bandwidth guardrail, not a preference — turning people away keeps the room working for everyone already in it.</Hint>

                    <CheckboxRow>
                        <input type="checkbox" checked={form.waitingRoom} onChange={(e) => set('waitingRoom', e.target.checked)} />
                        <span>Waiting room — the host admits each arrival</span>
                    </CheckboxRow>

                    <SectionLabel>How people arrive</SectionLabel>
                    <Hint>
                        Separate from the policies above, and both have to be open before somebody is heard. The policy decides whether they may EVER
                        speak; these decide whether they start that way.
                    </Hint>
                    <CheckboxRow>
                        <input type="checkbox" checked={form.participantJoinMuted} onChange={(e) => set('participantJoinMuted', e.target.checked)} />
                        <span>Arrive muted</span>
                    </CheckboxRow>
                    <CheckboxRow>
                        <input
                            type="checkbox"
                            checked={form.participantJoinCameraOff}
                            onChange={(e) => set('participantJoinCameraOff', e.target.checked)}
                        />
                        <span>Arrive with camera off</span>
                    </CheckboxRow>

                    <SectionLabel>What the room allows</SectionLabel>
                    <CheckboxRow>
                        <input type="checkbox" checked={form.raiseHandEnabled} onChange={(e) => set('raiseHandEnabled', e.target.checked)} />
                        <span>Raise hand — how somebody asks for the floor when audio is on request</span>
                    </CheckboxRow>
                    <CheckboxRow>
                        <input type="checkbox" checked={form.reactionsEnabled} onChange={(e) => set('reactionsEnabled', e.target.checked)} />
                        <span>Closing reactions</span>
                    </CheckboxRow>
                    <CheckboxRow>
                        <input type="checkbox" checked={form.chatEnabled} onChange={(e) => set('chatEnabled', e.target.checked)} />
                        <span>Chat</span>
                    </CheckboxRow>
                </MaterialFormContainer>
            }
        />
    );
};

const SectionLabel = styled.h5`
    margin: 20px 0 6px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.02em;
    opacity: 0.85;
`;

const Hint = styled.p`
    margin: 4px 0 12px;
    font-size: 12px;
    opacity: 0.65;
`;

const SectionTitle = styled.p`
    margin: 18px 0 4px;
    font-weight: 600;
`;

const ErrorBox = styled.div`
    background: rgba(255, 107, 107, 0.12);
    color: #ff8a8a;
    border: 1px solid rgba(255, 107, 107, 0.35);
    padding: 10px;
    border-radius: 4px;
    margin-bottom: 12px;
`;

const CheckboxRow = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
`;

export default CreateSessionModal;
